/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback } from "react";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { Key } from "ts-key-enum";
import { IListItemProps, ListItem } from "../ListItem";

export interface ISelectItemProps extends IListItemProps {
	id?: string;
	value?: string;
	className?: string;
	selected?: boolean;
	autoFocus?: boolean;
	disabled?: boolean;
	onKeyDown?: React.KeyboardEventHandler<HTMLLIElement>;
}

export const SelectItem: FC<ISelectItemProps> = ({
	children,
	value,
	id,
	className,
	selected,
	onSelect,
	autoFocus,
	onKeyDown,
	tabIndex,
	disabled,
	...props
}) => {
	const baseClass = BEM("edt-select-item", null, {
		selected,
		disabled
	});

	const selectOption = useCallback(
		(event: React.SyntheticEvent<HTMLLIElement>) => {
			if (disabled) {
				return;
			}
			const value = event.currentTarget?.dataset?.value;

			if (isFunction(onSelect)) {
				type TOnSelect = (value: string) => void;
				(onSelect as unknown as TOnSelect)(value);
			}
		},
		[ onSelect, disabled ]
	);

	const handleOptionKeyDown = useCallback<React.KeyboardEventHandler<HTMLLIElement>>(
		(event) => {
			if (event.key === " " || event.key === Key.Enter) {
				event.preventDefault();
				selectOption(event);
			}

			if (isFunction(onKeyDown)) {
				onKeyDown(event);
			}
		},
		[ selectOption, onKeyDown ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<ListItem
			role="option"
			id={id}
			autoFocus={autoFocus}
			tabIndex={disabled ? undefined : tabIndex}
			className={baseClass.toClassName()}
			aria-selected={selected}
			onClick={selectOption}
			onKeyDown={handleOptionKeyDown}
			data-value={value}
			disabled={disabled}
			{...props}
		>
			{children}
		</ListItem>
	);
};

SelectItem.defaultProps = {
	tabIndex: -1
};
